import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";

import StoreIcon from "@mui/icons-material/Store";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../../modals/AnimatedModal";
import { ButtonSuccessNoShadow } from "../../styled/button";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import MerchantsService from "../../../api/merchant";
import { setMerchantIfoodIds } from "../../../store/actions/merchantsSlice";
import {
  getDeliveryCenterConfig,
  setDeliveryCenterConfig,
} from "../../../api/DeliveryCenter/deliveryCenter";

export default function IfoodChatManagerModal({ show, closeModal }) {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchants, setSelectedMerchants] = useState([]);

  useEffect(() => {
    if (!show) return;
    loadMerchants();
  }, [show]);

  const loadMerchants = async () => {
    try {
      setLoading(true);
      await loadConfig();

      const params = { ifood: true };
      const { data } = await MerchantsService.getMerchants(params);
      setMerchants(data);
    } catch (err) {
      console.error(err);
      setErrorMessage("Ocorreu um erro ao carregar as lojas. Tente novamente.");
      setShowError(true);
      dispatchSnackBarMessage(
        "ifood-merchants-load-error",
        "Erro ao carregar lojas do iFood",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (merchantId) => {
    if (selectedMerchants.includes(merchantId)) {
      setSelectedMerchants(selectedMerchants.filter((id) => id !== merchantId));
    } else if (selectedMerchants.length < 10) {
      setSelectedMerchants([...selectedMerchants, merchantId]);
    }
  };

  const loadConfig = async () => {
    try {
      const params = {
        config: "ifood_chat_merchant_ids",
      };
      const { data } = await getDeliveryCenterConfig(params);

      if (data && !data.ifood_chat_merchant_ids) {
        return;
      }

      console.log("data", data);

      setSelectedMerchants(data.ifood_chat_merchant_ids);
    } catch (err) {
      console.log(err);
      dispatchSnackBarMessage(
        "config-load-error",
        "Erro ao carregar configurações",
        "error"
      );
    }
  };

  const handleSubmit = async () => {
    try {
      const data = {
        config: {
          ifood_chat_merchant_ids: selectedMerchants,
        },
      };

      await setDeliveryCenterConfig(data);

      dispatch(setMerchantIfoodIds(selectedMerchants));
      dispatchSnackBarMessage(
        "ifood-chat-config-success",
        "Configuração do chat iFood atualizada com sucesso",
        "success"
      );
      closeModal();
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        "ifood-chat-config-error",
        "Erro ao atualizar configuração do chat iFood",
        "error"
      );
    }
  };

  const renderMerchantItems = () => {
    return merchants.map((merchant) => {
      const isSelected = selectedMerchants.includes(merchant.ifood_id);

      return (
        <Paper
          key={merchant.id}
          elevation={3}
          sx={{
            mb: 2,
            overflow: "hidden",
            borderRadius: 2,
            transition: "all 0.3s ease",
            border: "1px solid",
            borderColor: isSelected
              ? alpha(theme.palette.success.main, 0.3)
              : alpha(theme.palette.grey[400], 0.3),
            cursor: "pointer",
          }}
          onClick={() => handleCheckboxChange(merchant.ifood_id)}
        >
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Background gradient */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                background: isSelected
                  ? `linear-gradient(45deg, ${theme.palette.success.light}, ${theme.palette.success.main})`
                  : `linear-gradient(45deg, ${theme.palette.grey[300]}, ${theme.palette.grey[400]})`,
                zIndex: 0,
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1, p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(
                      isSelected
                        ? theme.palette.success.main
                        : theme.palette.grey[500],
                      0.1
                    ),
                    color: isSelected
                      ? theme.palette.success.main
                      : theme.palette.grey[600],
                    borderRadius: 1.5,
                    p: 0.75,
                    width: 36,
                    height: 36,
                    mr: 2,
                  }}
                >
                  {isSelected ? (
                    <CheckCircleIcon fontSize="small" />
                  ) : (
                    <CancelIcon fontSize="small" />
                  )}
                </Box>

                <Typography variant="subtitle1" fontWeight={500}>
                  {merchant.name}
                </Typography>

                <Box sx={{ ml: "auto" }}>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleCheckboxChange(merchant.ifood_id)}
                    color={isSelected ? "success" : "default"}
                    disabled={!isSelected && selectedMerchants.length >= 10}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      );
    });
  };

  return (
    <>
      <AnimatedModal
        isOpen={show}
        onClose={closeModal}
        width="600px"
        maxWidth="90%"
      >
        <ModalHeader
          title="Lojas para o chat iFood"
          icon={StoreIcon}
          onClose={closeModal}
        />

        <ModalContent>
          <Box sx={{ px: 3, pt: 2, pb: 4 }}>
            <AnimatePresence>
              {showError && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Alert
                    severity="error"
                    onClose={() => setShowError(false)}
                    sx={{ mb: 3, borderRadius: 2 }}
                  >
                    {errorMessage}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: alpha(theme.palette.info.light, 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" component="p">
                Só podemos ter no máximo 10 lojas integradas ao chat iFood no
                Slapie, selecione as lojas que deseja ter acesso ao chat com
                clientes diretamente pelo Slapie
              </Typography>
            </Paper>

            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 2 }}>
              Lojas selecionadas: {selectedMerchants.length}/10
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              renderMerchantItems()
            )}

            <ButtonSuccessNoShadow
              fullWidth
              onClick={handleSubmit}
              disabled={selectedMerchants.length === 0 || loading}
              sx={{ mt: 3 }}
            >
              Confirmar
            </ButtonSuccessNoShadow>
          </Box>
        </ModalContent>
      </AnimatedModal>
    </>
  );
}
